import { useEffect, useState } from "react";

import HistoryIcon from "@mui/icons-material/History";
import {
    FormControl,
    FormLabel,
    FormGroup,
    Stack,
    Box,
    Button,
    Menu,
    MenuItem,
} from "@mui/material";

import { DateTime, DurationObjectUnits } from "luxon";

import { DateRangeDateTimePicker } from "./DateRangeDateTimePicker";
import { DateRangeLastUnitOfTimeDialog } from "./DateRangeLastUnitOfTimeDialog";
import { luxonUtils } from "utils/luxonUtils";

export interface DateRangeControlsProps {
    defaultSelectedStartDateTime?: string;
    defaultSelectedEndDateTime?: string;
    onRangeChange: (
        isValid: boolean,
        startDateTime: DateTime | null | undefined,
        endDateTime: DateTime | null | undefined,
    ) => void;
    maxNumberOfDays?: number;
    required?: boolean;
}

const validateDateRange = (
    startDateTime: DateTime | null | undefined,
    endDateTime: DateTime | null | undefined,
    maxNumberOfDays?: number,
) => {
    const errorResult: {
        startDateTime?: string;
        endDateTime?: string;
        isValid: boolean;
    } = { isValid: false };

    const invalidStartDateTime = !startDateTime || !startDateTime.isValid;
    const invalidEndDateTime = !endDateTime || !endDateTime.isValid;

    if (invalidStartDateTime || invalidEndDateTime) {
        errorResult.startDateTime = invalidStartDateTime
            ? "Invalid date specified"
            : undefined;
        errorResult.endDateTime = invalidEndDateTime
            ? "Invalid date specified"
            : undefined;
    } else {
        if (startDateTime >= endDateTime) {
            errorResult.startDateTime = "Date must be before TO date";
        }

        if (endDateTime <= startDateTime) {
            errorResult.endDateTime = "Date must be after FROM date";
        }

        if (
            maxNumberOfDays &&
            endDateTime.diff(startDateTime).as("days") > maxNumberOfDays
        ) {
            errorResult.endDateTime = `Your selection is bigger than ${maxNumberOfDays} days, please correct`;
        }
    }

    errorResult.isValid =
        !errorResult.startDateTime && !errorResult.endDateTime;

    return errorResult;
};

export function DateRangeControls(props: DateRangeControlsProps) {
    const [openRangeDialog, setOpenRangeDialog] = useState(false);

    const [startDateTime, setStartDateTime] = useState<DateTime | null>(
        luxonUtils.fromIso(props.defaultSelectedStartDateTime, false) ?? null,
    );
    const [endDateTime, setEndDateTime] = useState<DateTime | null>(
        luxonUtils.fromIso(props.defaultSelectedEndDateTime, false) ?? null,
    );

    const [startDateTimeError, setStartDateTimeError] = useState<
        string | undefined
    >();
    const [endDateTimeError, setEndDateTimeError] = useState<
        string | undefined
    >();

    useEffect(() => {
        const validationResult = validateDateRange(
            startDateTime,
            endDateTime,
            props.maxNumberOfDays,
        );

        setStartDateTimeError(validationResult.startDateTime);
        setEndDateTimeError(validationResult.endDateTime);

        props.onRangeChange(
            validationResult.isValid,
            startDateTime,
            endDateTime,
        );
    }, [startDateTime, endDateTime, props.maxNumberOfDays]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const onClickQuickSelection = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setAnchorEl(event.currentTarget);
    };
    const onCloseQuickSelection = () => {
        setAnchorEl(null);
    };

    const createPresetRangeChange = (
        timeAmount: number,
        unitOfTime: keyof DurationObjectUnits,
    ) => {
        return () => {
            const endDateTime = luxonUtils.resetSecondsAndMilliseconds(
                DateTime.now(),
            );
            const startDateTime = endDateTime.minus({
                [unitOfTime]: timeAmount,
            });

            setStartDateTime(startDateTime);
            setEndDateTime(endDateTime);

            onCloseQuickSelection();
        };
    };

    return (
        <>
            <DateRangeLastUnitOfTimeDialog
                open={openRangeDialog}
                onCancel={() => setOpenRangeDialog(false)}
                onSubmit={(startDateTime, endDateTime) => {
                    setOpenRangeDialog(false);

                    setStartDateTime(startDateTime);
                    setEndDateTime(endDateTime);
                }}
            />
            <FormControl
                required={props.required}
                component="fieldset"
                variant="standard"
                sx={{ paddingTop: 2 }}
            >
                <FormLabel>Date range</FormLabel>
                <FormGroup sx={{ paddingTop: 2 }}>
                    <Stack
                        direction="row"
                        spacing={2}
                    >
                        <DateRangeDateTimePicker
                            label="From"
                            value={startDateTime}
                            error={startDateTimeError}
                            onChange={setStartDateTime}
                        />
                        <DateRangeDateTimePicker
                            label="To"
                            value={endDateTime}
                            error={endDateTimeError}
                            onChange={setEndDateTime}
                        />
                        <Box>
                            <Button
                                variant="text"
                                startIcon={<HistoryIcon />}
                                onClick={onClickQuickSelection}
                            >
                                Presets
                            </Button>
                        </Box>
                    </Stack>
                </FormGroup>
            </FormControl>
            <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={onCloseQuickSelection}
            >
                <MenuItem onClick={createPresetRangeChange(15, "minutes")}>
                    15 min
                </MenuItem>
                <MenuItem onClick={createPresetRangeChange(30, "minutes")}>
                    30 min
                </MenuItem>
                <MenuItem onClick={createPresetRangeChange(1, "hours")}>
                    1 hour
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setOpenRangeDialog(true);
                        onCloseQuickSelection();
                    }}
                >
                    Custom...
                </MenuItem>
            </Menu>
        </>
    );
}
